import { unit_enum } from '@predium/enums';
import { isNil } from 'lodash';
import { UnitsValue } from './units';

type LocalizeValue = string | number | null | undefined;
type FormatAsBaseOptions = {
  defaultString?: string;
  unit?: UnitsValue;
  signDisplay?: Intl.NumberFormatOptions['signDisplay'];
  roundToNearestHundred?: boolean;
};
type FormatAsFloat = Omit<FormatAsBaseOptions, 'roundToNearestHundred'> & { fractionDigits?: 1 | 2 | 3 | 4 | 5 };
type FormatAsPercentage = Omit<FormatAsBaseOptions, 'unit' | 'roundToNearestHundred'> & {
  fractionDigits?: 0 | 1 | 2;
};
type FormatAsCurrency = Omit<FormatAsBaseOptions, 'unit'> & {
  currency?: 'EUR';
  fractionDigits?: 0 | 1 | 2;
  displayCompact?: boolean;
  perUnit?: UnitsValue;
  hideCurrencySymbol?: boolean;
};
type FormatAsDataSize = Omit<FormatAsBaseOptions, 'unit' | 'signDisplay' | 'roundToNearestHundred'>;
type FormatAsCompact = FormatAsBaseOptions & {
  fractionDigits?: 0 | 1 | 2 | 3;
};

const GERMAN_NUMBER_REGEX = /^\d{1,3}(\.\d{3})*(,\d+)?$/;
const ENGLISH_GBNUMBER_REGEX = /^\d{1,3}(,\d{3})*(\.\d+)?$/;

export class Localize<TTranslationFunction extends (...args: any[]) => any = (...args: any[]) => any> {
  private _locale: string | (() => string) = 'de-DE';
  private _t: TTranslationFunction;
  private _isBackend = false;

  constructor(locale: string | (() => string), t: TTranslationFunction, isBackend = false) {
    this._locale = locale;
    this._t = t;
    this._isBackend = isBackend;
  }

  setLocale(locale: string | (() => string)) {
    if (typeof locale === typeof this._locale) {
      this._locale = locale;
    } else {
      throw new Error(
        `Changing the locale type is not allowed. Current locale type: ${typeof this
          ._locale}. New locale type: ${typeof locale}`,
      );
    }
  }

  get locale(): string {
    if (typeof this._locale === 'string') {
      return this._locale;
    }

    return this._locale();
  }

  private removeFormatting(numStr: string, locale: 'de-DE' | 'en-GB') {
    switch (locale) {
      case 'de-DE':
        return numStr.replace(/\./g, '').replace(',', '.');
      case 'en-GB':
        return numStr.replace(/,/g, '');
      default:
        return numStr;
    }
  }

  private isNumber(value: LocalizeValue): [boolean, number] {
    const parsedValue = value === null ? undefined : value === '' ? undefined : value;

    // WHY: some places using Localize are passing already formatted numbers coming from number inputs.
    // Fixing those cases at the root level would take too much time and effort.
    const isGermanFormattedValue = typeof parsedValue === 'string' && GERMAN_NUMBER_REGEX.test(parsedValue);
    const isEnglishGbFormattedValue = typeof parsedValue === 'string' && ENGLISH_GBNUMBER_REGEX.test(parsedValue);
    const unformattedValue = isGermanFormattedValue
      ? this.removeFormatting(parsedValue, 'de-DE')
      : isEnglishGbFormattedValue
        ? this.removeFormatting(parsedValue, 'en-GB')
        : parsedValue;

    const numberValue = Number(unformattedValue);
    return [!isNil(value) && !Number.isNaN(numberValue), numberValue] as const;
  }

  private parseUnit(value: number, unit?: UnitsValue) {
    if (!unit) return '';
    if (unit === unit_enum.YEAR) {
      const valueQuantity = Number.isNaN(value) ? 0 : Math.abs(value);

      if (this._isBackend) {
        return valueQuantity === 1 ? this._t('general.year') : this._t('general.years');
      }

      return ` ${this._t('General_Year', { count: valueQuantity })}`;
    }

    if (this._isBackend) {
      return ` ${this._t(`general.Enum_UnitEnum-${unit_enum[unit]}`)}`;
    }

    return ` ${this._t(`Enum_UnitEnum-${unit_enum[unit]}`)}`;
  }

  private parsePerUnit(perUnitType?: UnitsValue) {
    if (!perUnitType) return '';
    if (this._isBackend) {
      return `/${this._t(`general.Enum_UnitEnum-${unit_enum[perUnitType]}`)}`;
    }
    return `/${this._t(`Enum_UnitEnum-${unit_enum[perUnitType]}`)}`;
  }

  private toDataSizeAndUnitType(value: number) {
    if (value >= 1e12) {
      return { dataSize: value / 1e12, unit: 'terabyte' };
    }
    if (value >= 1e9) {
      return { dataSize: value / 1e9, unit: 'gigabyte' };
    }
    if (value >= 1e6) {
      return { dataSize: value / 1e6, unit: 'megabyte' };
    }
    if (value >= 1e3) {
      return { dataSize: value / 1e3, unit: 'kilobyte' };
    }
    return { dataSize: value, unit: 'byte' };
  }

  private getCompactFractionDigits(value: number, fractionDigits: number) {
    const isThousands = Math.abs(value) >= 1_000 && Math.abs(value) < 1_000_000;

    // skip showing minimum fraction digits for German in the thousands range
    if (isThousands && this.locale === 'de-DE') return 0;

    const denominator = (() => {
      if (Math.abs(value) >= 0 && Math.abs(value) < 1_00) return 1; // tens
      if (Math.abs(value) >= 1_00 && Math.abs(value) < 1_000) return 1_00; // hundreds
      if (Math.abs(value) >= 1_000 && Math.abs(value) < 1_000_000) return 1_000; // thousands
      if (Math.abs(value) >= 1_000_000 && Math.abs(value) < 1_000_000_000) return 1_000_000; // millions
      if (Math.abs(value) >= 1_000_000_000 && Math.abs(value) < 1_000_000_000_000) return 1_000_000_000; // billions
      return 1_000_000_000_000; // quadrillions
    })();

    // hide fraction digits if compacted value is an integer in their range
    // i.e. in English if value is 151,000 and fractionDigits = 1, then 151K
    return (value / denominator) % 1 === 0 ? 0 : fractionDigits;
  }

  private getFractionDigitOptions(value: number, fractionDigits: number, isCurrency = false, isCompacted = false) {
    if (isCompacted) {
      return {
        minimumFractionDigits: this.getCompactFractionDigits(value, fractionDigits),
        maximumFractionDigits: this.getCompactFractionDigits(value, fractionDigits),
      } as const;
    }

    if (isCurrency) {
      // hide fraction digits if value is an integer
      // i.e. if value is 9, then fractionDigits = 2 will display 9 instead of 9.00
      const digits = value % 1 === 0 ? 0 : fractionDigits;
      return {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      } as const;
    }

    return {
      maximumFractionDigits: fractionDigits,
    } as const;
  }

  private getNumberFormatCompactOptions() {
    return {
      notation: 'compact',
      compactDisplay: 'short',
      useGrouping: true,
      trailingZeroDisplay: 'stripIfInteger',
    } as const;
  }

  /**
   * Normalize compact number suffixes to CLDR 43+ en-GB canonical form (k, m, bn, tn).
   *
   * Intl.NumberFormat compact output for en-GB varies by ICU/CLDR version:
   * - ICU < 72 (Node ≤ 21, macOS system ICU): produces K, M, B, T
   * - ICU ≥ 72 / CLDR 43+ (Node 22+, Linux CI): produces k, m, bn, tn
   *
   * We always normalize to the modern CLDR 43+ form so output is identical
   * regardless of environment. The German locale is unaffected — it uses
   * Mio./Mrd./Bio. with a non-breaking space separator, so none of these
   * patterns match German-formatted strings.
   */
  static normalizeCompactSuffix(value: string): string {
    return value.replace(/(\d)K/g, '$1k').replace(/(\d)M/g, '$1m').replace(/(\d)B/g, '$1bn').replace(/(\d)T/g, '$1tn');
  }

  static roundToNearestHundred(value: number, shouldRound: boolean) {
    if (shouldRound && Math.abs(value) > 100) {
      const hundreds = value / 100;
      const decimalFraction = hundreds % 1;
      const sign = Math.sign(value);

      return Math.round(Math.abs(decimalFraction) * 10) / 10 <= 0.5
        ? Math.floor(Math.abs(hundreds)) * 100 * sign
        : Math.ceil(Math.abs(hundreds)) * 100 * sign;
    }
    return value;
  }

  /**
   * Format a value as an integer using the locale. The value is
   * coerced to a number and then formatted according to the
   * locale. If value is a float, it will be rounded to an integer using "halfExpand" rounding mode.
   *
   * @param value - The value to format.
   * @param options - Options for formatting the value.
   * @param options.defaultString - The default string to return if the value is null or undefined or if parsing to a
   *   Number return NaN.
   * @param options.unit - The unit to append to the formatted value.
   * @param options.signDisplay - The sign display option for the formatter (default is 'auto').
   * @param options.roundToNearestHundred - Rounds up the value to the nearest hundred.
   * @returns {string} The formatted value.
   */
  formatAsInteger(
    value: LocalizeValue,
    {
      defaultString = '–',
      unit,
      signDisplay = 'auto',
      roundToNearestHundred = false,
    }: Partial<FormatAsBaseOptions> = {},
  ): string {
    const [isNumberValue, numberValue] = this.isNumber(value);
    const parsedUnit = this.parseUnit(numberValue, unit);
    if (!isNumberValue) {
      return `${defaultString}${parsedUnit}`;
    }

    const roundedNumber = Localize.roundToNearestHundred(numberValue, roundToNearestHundred);
    const formattedValue = new Intl.NumberFormat(this.locale, {
      maximumFractionDigits: 0,
      signDisplay: roundedNumber === 0 ? 'never' : signDisplay,
    }).format(roundedNumber);

    return `${formattedValue}${parsedUnit}`;
  }

  /**
   * Format a value as a float with one fractional digit as default using the locale. The value is
   * coerced to a number and then formatted according to the
   * locale.
   *
   * @param value - The value to format.
   * @param options - Options for formatting the value.
   * @param options.defaultString - The default string to return if the value is null or undefined or if parsing to a
   *   Number return NaN.
   * @param options.unit - The unit to append to the formatted value.
   * @param options.fractionDigits - The number of fractional digits to display when value is not an integer (default
   *   is 2 i.e. 9.00)
   * @param options.signDisplay - The sign display option for the formatter (default is 'auto').
   * @returns {string} The formatted value.
   */
  formatAsFloat(
    value: LocalizeValue,
    { defaultString = '–', unit, fractionDigits = 2, signDisplay = 'auto' }: Partial<FormatAsFloat> = {},
  ): string {
    const [isNumberValue, numberValue] = this.isNumber(value);
    const parsedUnit = this.parseUnit(numberValue, unit);
    if (!isNumberValue) {
      return `${defaultString}${parsedUnit}`;
    }

    const formattedValue = new Intl.NumberFormat(this.locale, {
      signDisplay: numberValue === 0 ? 'never' : signDisplay,
      ...this.getFractionDigitOptions(numberValue, fractionDigits),
    }).format(numberValue);

    return `${formattedValue}${parsedUnit}`;
  }

  /**
   * Format a value as a percentage using the locale. The value is
   * coerced to a number and then formatted according to the
   * locale.
   *
   * @param value - The value to format.
   * @param options - Options for formatting the value.
   * @param options.defaultString - The default string to return if the value is null or undefined or if parsing to a
   *   Number return NaN.
   * @param options.fractionDigits - The number of fractional digits to display when value is not an integer (default
   *   is 2 i.e. 9.00)
   * @param options.signDisplay - The sign display option for the formatter (default is 'auto').
   * @returns {string} The formatted value.
   */
  formatAsPercentage(
    value: LocalizeValue,
    { defaultString = '–', fractionDigits = 2, signDisplay = 'auto' }: Partial<FormatAsPercentage> = {},
  ): string {
    const [isNumberValue, numberValue] = this.isNumber(value);
    if (!isNumberValue) {
      return new Intl.NumberFormat(this.locale, {
        style: 'percent',
        signDisplay,
        ...this.getFractionDigitOptions(numberValue, fractionDigits),
      })
        .format(numberValue)
        .replace(String(NaN), defaultString);
    }

    return new Intl.NumberFormat(this.locale, {
      style: 'percent',
      signDisplay: numberValue === 0 ? 'never' : signDisplay,
      ...this.getFractionDigitOptions(numberValue, fractionDigits),
    }).format(numberValue / 100);
  }

  /**
   * Format a value as currency using the locale. The default currency is the Euro (EUR). The value is
   * coerced to a number and then formatted according to the
   * locale.
   *
   * @param value - The value to format.
   * @param options - Options for formatting the value.
   * @param options.defaultString - The default string to return if the value is null or undefined or if parsing to a
   *   Number return NaN.
   * @param options.fractionDigits - The number of fractional digits to display when value is not an integer (default
   *   is 2 i.e. 9.00)
   * @param options.currency - The currency to format the value in. Default is 'EUR'.
   * @param options.roundToNearestHundred - Rounds up the value to the nearest hundred.
   * @param options.displayCompact - Whether to display the value in compact format.
   * @param options.perUnit - A unit, i.e. m², to be displayed as the denominator of the currency i.e. "€/m²"
   * @param options.signDisplay - The sign display option for the formatter (default is 'auto').
   * @returns {string} The formatted value.
   */
  formatAsCurrency(
    value: LocalizeValue,
    {
      defaultString = '–',
      fractionDigits = 2,
      currency = 'EUR',
      roundToNearestHundred = false,
      displayCompact = false,
      perUnit,
      signDisplay,
      hideCurrencySymbol = false,
    }: Partial<FormatAsCurrency> = {},
  ): string {
    const [isNumberValue, numberValue] = this.isNumber(value);
    const parsedPerUnit = this.parsePerUnit(perUnit);

    if (!isNumberValue) {
      const formattedValue = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency,
        signDisplay,
        ...this.getFractionDigitOptions(numberValue, fractionDigits),
      })
        .format(numberValue)
        .replace(String(NaN), defaultString);

      const finalValue = hideCurrencySymbol ? formattedValue.replace(/[€$£¥₹]/g, '').trim() : formattedValue;

      return `${finalValue}${parsedPerUnit}`;
    }

    const roundedNumber = Localize.roundToNearestHundred(numberValue, roundToNearestHundred);
    const compactDisplayOptions = displayCompact ? this.getNumberFormatCompactOptions() : {};

    const formattedValue = new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency,
      signDisplay: roundedNumber === 0 ? 'never' : signDisplay,
      ...this.getFractionDigitOptions(roundedNumber, fractionDigits, true, displayCompact),
      ...compactDisplayOptions,
    }).format(roundedNumber);

    const finalValue = hideCurrencySymbol ? formattedValue.replace(/[€$£¥₹]/g, '').trim() : formattedValue;
    const normalizedValue = displayCompact ? Localize.normalizeCompactSuffix(finalValue) : finalValue;

    return `${normalizedValue}${parsedPerUnit}`;
  }

  /**
   * Format a value as a data size using the locale based on the value size. The value is
   * coerced to a number and then formatted according to the
   * locale.
   *
   * @param value - The value to format.
   * @param options - Options for formatting the value.
   * @param options.defaultString - The default string to return if the value is null or undefined or if parsing to a
   *   Number return NaN.
   * @returns {string} The formatted value.
   */
  formatAsDataSize(value: LocalizeValue, { defaultString = '–' }: Partial<FormatAsDataSize> = {}): string {
    const [isNumberValue, numberValue] = this.isNumber(value);
    if (!isNumberValue) {
      return new Intl.NumberFormat(this.locale, {
        style: 'unit',
        unit: 'byte',
        ...this.getFractionDigitOptions(numberValue, 1),
      })
        .format(numberValue)
        .replace(String(NaN), defaultString);
    }

    const { dataSize, unit } = this.toDataSizeAndUnitType(numberValue);
    return new Intl.NumberFormat(this.locale, {
      style: 'unit',
      unit,
      ...this.getFractionDigitOptions(dataSize, 1),
    }).format(dataSize);
  }

  /**
   * Format a value as compact (abbreviated) number using the locale. The value is
   * coerced to a number and then formatted according to the
   * locale.
   *
   * @param value - The value to format.
   * @param options - Options for formatting the value.
   * @param options.defaultString - The default string to return if the value is null or undefined or if parsing to a
   *   Number return NaN.
   * @param options.unit - The unit to append to the formatted value.
   * @param options.fractionDigits - The number of fractional digits to display when value is not an integer (default
   *   is 2 i.e. 9.00)
   * @param options.signDisplay - The sign display option for the formatter (default is 'auto').
   * @param options.roundToNearestHundred - Rounds up the value to the nearest hundred.
   * @returns {string} The formatted value.
   */
  formatAsCompact(
    value: LocalizeValue,
    {
      defaultString = '–',
      unit,
      fractionDigits = 1,
      signDisplay,
      roundToNearestHundred = false,
    }: Partial<FormatAsCompact> = {},
  ): string {
    const [isNumberValue, numberValue] = this.isNumber(value);
    const parsedUnit = this.parseUnit(numberValue, unit);
    if (!isNumberValue) {
      return `${defaultString}${parsedUnit}`;
    }

    const roundedNumber = Localize.roundToNearestHundred(numberValue, roundToNearestHundred);

    const formattedValue = new Intl.NumberFormat(this.locale, {
      signDisplay: roundedNumber === 0 ? 'never' : signDisplay,
      ...this.getNumberFormatCompactOptions(),
      ...this.getFractionDigitOptions(roundedNumber, fractionDigits, false, true),
    }).format(roundedNumber);

    return `${Localize.normalizeCompactSuffix(formattedValue)}${parsedUnit}`;
  }
}
